import React from "react";
import { Link } from "react-router-dom";

/**
 * Shared page hero.
 *
 * Layout follows the one finding that verified strongly in our CRO research:
 * NN/g eyetracking (130k+ fixations) puts 42% of viewing time in the top 20%
 * of the page and 65% in the top 40%. So headline, subhead, the single primary
 * CTA and a proof line all sit in the upper half of the first screen — nothing
 * load-bearing is pushed down.
 *
 * `ctaLabel` must describe the destination. NN/g usability testing found
 * generic labels ("Get Started", "Learn more") get clicked but mislead users
 * into unexpected funnels, costing trust and completions — clicks go up while
 * qualified leads go down. Prefer "Book a 30-minute consultation".
 */
const PageHero = ({
  eyebrow,
  title,
  subtitle,
  ctaLabel,
  ctaTo = "/contact",
  ctaHref,
  secondaryLabel,
  secondaryTo,
  secondaryHref,
  proofLine,
  backgroundImage,
  align = "left",
}) => {
  const centered = align === "center";

  // `ctaHref` / `secondaryHref` cover same-page anchors (/contact scrolling to
  // its own form). A router <Link to="#form"> would push "/#form" and bounce to
  // the home page, so those cases need a plain <a>.
  const ctaClass =
    "inline-block rounded-md bg-moss px-6 py-3 text-base font-medium text-black transition-colors duration-300 hover:bg-moss-light";
  const secondaryClass =
    "group relative inline-block py-3 text-base font-medium text-silver-light transition-colors duration-300 hover:text-white";
  const secondaryUnderline = (
    <span className="absolute bottom-1 left-0 block h-px w-full origin-left scale-x-0 bg-moss transition-transform duration-300 ease-out group-hover:scale-x-100" />
  );

  return (
    <section className="relative -mt-16 overflow-hidden">
      {backgroundImage && (
        <div className="absolute inset-0">
          <img
            src={backgroundImage}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-center"
          />
          {/* Flat base below md: on narrow screens copy spans the full width and
              a directional fade alone leaves it on bright pixels. */}
          <div className="absolute inset-0 bg-black/55 md:hidden" />
          <div
            className={`absolute inset-0 ${centered
              ? "bg-black/70"
              : "bg-gradient-to-r from-black via-black/70 to-transparent"
              }`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50" />
        </div>
      )}

      <div className="relative z-10 container mx-auto px-4 pt-40 pb-16 md:pt-48 md:pb-24">
        <div
          className={`max-w-3xl ${centered ? "mx-auto text-center" : ""}`}
        >
          {eyebrow && (
            <p className="text-moss-light text-sm font-medium tracking-[0.2em] uppercase mb-4">
              {eyebrow}
            </p>
          )}

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium leading-tight mb-6 text-silver-light">
            {title}
          </h1>

          {subtitle && (
            <p className="text-lg md:text-xl text-silver-medium mb-8 leading-relaxed">
              {subtitle}
            </p>
          )}

          <div
            className={`flex flex-wrap items-center gap-4 ${centered ? "justify-center" : ""
              }`}
          >
            {ctaLabel &&
              (ctaHref ? (
                <a href={ctaHref} className={ctaClass}>
                  {ctaLabel}
                </a>
              ) : (
                <Link to={ctaTo} className={ctaClass}>
                  {ctaLabel}
                </Link>
              ))}
            {secondaryLabel &&
              (secondaryHref ? (
                <a href={secondaryHref} className={secondaryClass}>
                  {secondaryLabel}
                  {secondaryUnderline}
                </a>
              ) : secondaryTo ? (
                <Link to={secondaryTo} className={secondaryClass}>
                  {secondaryLabel}
                  {secondaryUnderline}
                </Link>
              ) : null)}
          </div>

          {proofLine && (
            <p className={`mt-6 text-sm text-silver-medium ${centered ? "" : ""}`}>
              {proofLine}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default PageHero;
